import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import ChevronRightLargeIcon from '@atlaskit/icon/core/chevron-right';
import ChevronLeftLargeIcon from '@atlaskit/icon/core/chevron-left';
import { Inline } from '@atlaskit/primitives/compiled';
import Button from '@atlaskit/button/new';

const i18n = defineMessages({
	previousButtonLabel: {
		id: 'confluence-analytics.analytics-table.pagination-previous-button-label',
		defaultMessage: 'Previous',
		description: 'Label for button that moves pagination back to the previous page',
	},
	nextButtonLabel: {
		id: 'confluence-analytics.analytics-table.pagination-next-button-label',
		defaultMessage: 'Next',
		description: 'Label for button that moves pagination to the next page',
	},
	pageResultsCountLabel: {
		id: 'confluence-analytics.analytics-table.pagination-page-results-count-label',
		defaultMessage: '{start} - {end} of all results',
		description: 'Label to indicate which items where shown on the page',
	},
	pageResultsCountWithTotalLabel: {
		id: 'confluence-analytics.analytics-table.pagination-page-results-count-with-total-label',
		defaultMessage: '{start} - {end} of {total, plural, one {# result} other {# results}}',
		description:
			'Label to indicate which items of total where shown on the page. This text is shown instead of "pagination-page-results-count-label" if we have the count of total items.',
	},
});

export type TablePaginationState = {
	offset: number;
	limit: number;
	total?: number;
	hasPrev?: boolean;
	hasNext?: boolean;
};

type AnalyticsTableOffsetPaginationProps = {
	paginationState: TablePaginationState;
	onPageChange: (paginationState: TablePaginationState) => void;
};

export const AnalyticsTableOffsetPagination = ({
	paginationState,
	onPageChange,
}: AnalyticsTableOffsetPaginationProps) => {
	const { formatMessage } = useIntl();
	const { offset, limit, total, hasPrev, hasNext } = paginationState;

	let nextOffset = offset + limit;

	if (total && nextOffset > total) {
		nextOffset = total;
	}

	return (
		<Inline alignBlock="center" space="space.100">
			<Button
				spacing="compact"
				iconBefore={(iconProps) => <ChevronLeftLargeIcon {...iconProps} size="small" />}
				isDisabled={!Boolean(hasPrev)}
				aria-label={formatMessage(i18n.previousButtonLabel)}
				onClick={() => {
					onPageChange({
						...paginationState,
						offset: offset - limit,
					});
				}}
			>
				<FormattedMessage {...i18n.previousButtonLabel} />
			</Button>
			<Button
				spacing="compact"
				iconAfter={(iconProps) => <ChevronRightLargeIcon {...iconProps} size="small" />}
				isDisabled={!Boolean(hasNext)}
				aria-label={formatMessage(i18n.nextButtonLabel)}
				onClick={() => {
					onPageChange({
						...paginationState,
						offset: nextOffset,
					});
				}}
			>
				<FormattedMessage {...i18n.nextButtonLabel} />
			</Button>
			{total ? (
				<FormattedMessage
					{...i18n.pageResultsCountWithTotalLabel}
					values={{
						start: offset,
						end: nextOffset,
						total,
					}}
				/>
			) : (
				<FormattedMessage
					{...i18n.pageResultsCountLabel}
					values={{
						start: offset,
						end: nextOffset,
					}}
				/>
			)}
		</Inline>
	);
};

export enum PageChangeDirection {
	PREV,
	NEXT,
}

export type AnalyticsTablePaginationProps = {
	hasPrev: boolean;
	hasNext: boolean;
	pageStart: number;
	pageEnd: number;
	onPageChange: (direction: PageChangeDirection) => void;
};

export const AnalyticsTablePagination = ({
	hasPrev,
	hasNext,
	pageStart,
	pageEnd,
	onPageChange,
}: AnalyticsTablePaginationProps) => {
	const { formatMessage } = useIntl();
	return (
		<Inline alignBlock="center" space="space.100">
			<Button
				spacing="compact"
				iconBefore={(iconProps) => <ChevronLeftLargeIcon {...iconProps} size="small" />}
				isDisabled={!hasPrev}
				aria-label={formatMessage(i18n.previousButtonLabel)}
				onClick={() => onPageChange(PageChangeDirection.PREV)}
			>
				<FormattedMessage {...i18n.previousButtonLabel} />
			</Button>
			<Button
				spacing="compact"
				iconAfter={(iconProps) => <ChevronRightLargeIcon {...iconProps} size="small" />}
				isDisabled={!hasNext}
				aria-label={formatMessage(i18n.nextButtonLabel)}
				onClick={() => onPageChange(PageChangeDirection.NEXT)}
			>
				<FormattedMessage {...i18n.nextButtonLabel} />
			</Button>
			<FormattedMessage
				{...i18n.pageResultsCountLabel}
				values={{
					start: pageStart,
					end: pageEnd,
				}}
			/>
		</Inline>
	);
};
